/**
 * Leftover unsaved work — whether there is any, where it lives, and what a prompt says about it.
 *
 * Two surfaces ask the same question. „+ Nowy program" asks it BEFORE the create modal (owner, 23
 * September 2026): a trainer who starts a new plan with an unsaved one still around is offered it
 * back first, rather than silently overwriting it with the next seed. `/plans/nowy` asks it when
 * the store is empty — after a reload — and offers to restore what the browser kept. Both read the
 * answer from `newPlanEntry`, and both draw it from `summariseLeftoverPlan`, so they cannot disagree
 * about what counts as leftover or about what a trainer is told of it.
 *
 * Pure: the tab's document, the judged record and `now` arrive as parameters.
 */

import { planCategoryKey, type PlanCategoryKey } from './planCategory';
import { planDisplayCoverUrl } from './planCover';
import type { PlanDraft } from './planDraft.types';
import type { UnsavedPlanRecipient, UnsavedPlanRecord } from './unsavedPlanRecord';

/**
 * The unsaved plan THIS TAB holds, with what the crash-recovery writer knows about it: which
 * document it is, and whether its latest write reached the device.
 */
export interface TabUnsavedPlan {
  readonly draft: PlanDraft;
  readonly documentKey: string;
  readonly held: boolean;
  /** The tab's own save marker — see `LeftoverPlan.saveStartedAt`. */
  readonly saveStartedAt: number | null;
  readonly recipient: UnsavedPlanRecipient | null;
}

export interface LeftoverPlan {
  /** The document to continue. */
  readonly draft: PlanDraft;
  /**
   * Whether the document store already holds it. Continuing it is then a navigation, never a load:
   * loading would replace the live tree with its debounced copy and could drop the last keystrokes.
   */
  readonly inStore: boolean;
  /** Which unsaved document this is — what a discard of THIS offer removes, and nothing else. */
  readonly documentKey: string | null;
  /** Whether the device holds a copy of it. A plan only in this tab's memory dies with the tab. */
  readonly held: boolean;
  /**
   * How long ago the device copy of THIS document was written, measured when the question was
   * asked — or `null` when the device holds none of it: storage blocked, a write refused, or the one
   * key on the device carrying another tab's plan. Measured once, so the line does not tick.
   */
  readonly age: LeftoverPlanAge | null;
  /**
   * When a first save of this plan was sent and never answered — the server may hold the plan
   * already. `null` when every save of it was answered, or none was sent. Carried through
   * „Kontynuuj" into the tab, so the warning survives continuing until a save is answered.
   */
  readonly saveStartedAt: number | null;
  /** The client the plan was being built for — carried back into the store when it is continued. */
  readonly recipient: UnsavedPlanRecipient | null;
}

export type NewPlanEntry =
  | { readonly kind: 'create' }
  | { readonly kind: 'resume'; readonly plan: LeftoverPlan };

/**
 * What „+ Nowy program" — and `/plans/nowy` on an empty store — opens with.
 *
 * The TAB wins over the record when it holds an unsaved plan, because it is the fresher of the two:
 * the record trails it by at most one debounce, and it may be another tab's plan altogether. Its age
 * is the record's only when the record is the same document — the card must not print the time of
 * a different plan under this one's name.
 */
export function newPlanEntry(
  tab: TabUnsavedPlan | null,
  record: UnsavedPlanRecord | null,
  now: number,
): NewPlanEntry {
  if (tab !== null) {
    const same = record !== null && record.documentKey === tab.documentKey ? record : null;
    return {
      kind: 'resume',
      plan: {
        draft: tab.draft,
        inStore: true,
        documentKey: tab.documentKey,
        held: tab.held,
        age: same === null ? null : leftoverPlanAge(same.savedAt, now),
        saveStartedAt: tab.saveStartedAt ?? same?.saveStartedAt ?? null,
        recipient: tab.recipient,
      },
    };
  }
  if (record !== null) {
    return {
      kind: 'resume',
      plan: {
        draft: record.draft,
        inStore: false,
        documentKey: record.documentKey,
        held: true,
        age: leftoverPlanAge(record.savedAt, now),
        saveStartedAt: record.saveStartedAt,
        recipient: record.recipient,
      },
    };
  }
  return { kind: 'create' };
}

/**
 * The line under the figures. `savedAt` when the device holds this plan's copy and can date it;
 * `tabOnly` when it holds none, because then a reload deletes the plan and the trainer has to know;
 * `none` for a plan kept on the device whose record another tab has since overwritten.
 */
export type LeftoverPlanFreshness =
  | { readonly kind: 'savedAt'; readonly age: LeftoverPlanAge }
  | { readonly kind: 'tabOnly' }
  | { readonly kind: 'none' };

export interface LeftoverPlanSummary {
  readonly name: string;
  readonly category: PlanCategoryKey | null;
  readonly coverUrl: string;
  readonly phases: number;
  /** Days that exist in the plan's structure — the unit the builder calls a training. */
  readonly trainings: number;
  readonly exercises: number;
  readonly freshness: LeftoverPlanFreshness;
  readonly saveMayHaveLanded: boolean;
  /** Who the plan was being built for, by name as the roster reads it — or `null` for a library plan. */
  readonly clientName: string | null;
}

/** Everything a prompt prints about the leftover plan, decided here rather than in its JSX. */
export function summariseLeftoverPlan(plan: LeftoverPlan): LeftoverPlanSummary {
  const days = plan.draft.phases.flatMap((phase) => phase.days);
  const category = planCategoryKey(plan.draft.category);
  return {
    name: plan.draft.name,
    category,
    coverUrl: planDisplayCoverUrl(plan.draft.coverImageUrl, category, plan.draft.name, 'thumb'),
    phases: plan.draft.phases.length,
    trainings: days.length,
    exercises: days.reduce((sum, day) => sum + day.exercises.length, 0),
    freshness:
      plan.age !== null
        ? { kind: 'savedAt', age: plan.age }
        : plan.held
          ? { kind: 'none' }
          : { kind: 'tabOnly' },
    saveMayHaveLanded: plan.saveStartedAt !== null,
    clientName: plan.recipient?.clientName ?? null,
  };
}

/** How long ago, as the unit and count `Intl.RelativeTimeFormat` takes. */
export interface LeftoverPlanAge {
  readonly unit: 'second' | 'minute' | 'hour' | 'day';
  readonly value: number;
}

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MS_PER_DAY = 86_400_000;

/** Local midnight of the day an instant falls on — what „wczoraj" counts from. */
function startOfLocalDay(at: number): number {
  const day = new Date(at);
  return new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
}

/**
 * The record's age at the grain a recovery needs. A crash recovery is usually minutes old, which is
 * why this is not the library's day-grained freshness: „dzisiaj" says nothing about which of two
 * sessions today the plan came from.
 *
 * Under a minute is `{ second, 0 }`, which `numeric: 'auto'` renders as „teraz" / "now". From a
 * day up the count is CALENDAR days, not 24-hour blocks: the formatter words it „wczoraj" and
 * „przedwczoraj", so 34 hours from 00:00 two days back must say two, not one. Rounded, because a
 * daylight-saving day is 23 or 25 hours long. A write stamped in the future — a clock moved back —
 * reads as now rather than as „za 3 minuty".
 */
export function leftoverPlanAge(savedAt: number, now: number): LeftoverPlanAge {
  const minutes = Math.floor(Math.max(0, now - savedAt) / MS_PER_MINUTE);
  if (minutes < 1) return { unit: 'second', value: 0 };
  if (minutes < MINUTES_PER_HOUR) return { unit: 'minute', value: minutes };
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  if (hours < HOURS_PER_DAY) return { unit: 'hour', value: hours };
  const days = Math.round((startOfLocalDay(now) - startOfLocalDay(savedAt)) / MS_PER_DAY);
  return { unit: 'day', value: Math.max(1, days) };
}
